/**
 * The three safety-pipeline illustrations shared by the home-page ProcessSection
 * and the /sicherheit page. Both rendered byte-identical copies before this module
 * existed (jscpd 2026-08-13: 19 clone fragments across the two files).
 *
 * Local to denqfabrik-landing on purpose — no other app renders them, and
 * `packages/CLAUDE.md` promotes at the *second* consumer, not preemptively.
 *
 * SicherheitPage additionally owns ContextGraphic and GuardrailsGraphic; those
 * have exactly one call site and stay there.
 */

/** Neural-net classifier: request in, confidence-scored class out. */
export function ClassificationGraphic() {
  return (
    <svg
      viewBox="0 0 320 260"
      className="h-full w-full"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Input box top */}
      <rect
        x={20}
        y={12}
        width={280}
        height={36}
        rx={8}
        fill="rgba(25,45,85,0.6)"
        stroke="rgba(79,141,255,0.2)"
        strokeWidth="1"
      />
      <text x={34} y={34} fill="rgba(192,212,238,0.5)" fontSize="11" fontFamily="monospace">
        request_payload.json
      </text>
      <rect
        x={230}
        y={20}
        width={56}
        height={20}
        rx={4}
        fill="rgba(45,212,191,0.15)"
        stroke="rgba(45,212,191,0.35)"
        strokeWidth="1"
      />
      <text
        x={258}
        y={34}
        textAnchor="middle"
        fill="rgba(45,212,191,0.8)"
        fontSize="9"
        fontFamily="monospace"
      >
        valid
      </text>

      {/* Arrow down */}
      <line x1={160} y1={48} x2={160} y2={68} stroke="rgba(79,141,255,0.25)" strokeWidth="1.5" />
      <polygon points="155,65 160,72 165,65" fill="rgba(79,141,255,0.25)" />

      {/* Neural network — 4 layers */}
      {/* Layer 1: 3 input nodes */}
      {[95, 135, 175].map((y, i) => (
        <circle
          key={`l0-${i}`}
          cx={60}
          cy={y}
          r={7}
          fill="rgba(79,141,255,0.08)"
          stroke="rgba(79,141,255,0.3)"
          strokeWidth="1"
        />
      ))}
      {/* Layer 2: 5 hidden nodes */}
      {[82, 110, 138, 166, 194].map((y, i) => (
        <circle
          key={`l1-${i}`}
          cx={130}
          cy={y}
          r={7}
          fill="rgba(79,141,255,0.08)"
          stroke="rgba(79,141,255,0.3)"
          strokeWidth="1"
        />
      ))}
      {/* Layer 3: 4 hidden nodes */}
      {[90, 122, 154, 186].map((y, i) => (
        <circle
          key={`l2-${i}`}
          cx={200}
          cy={y}
          r={7}
          fill="rgba(79,141,255,0.08)"
          stroke="rgba(79,141,255,0.3)"
          strokeWidth="1"
        />
      ))}
      {/* Layer 4: 2 output nodes */}
      {[120, 160].map((y, i) => (
        <circle
          key={`l3-${i}`}
          cx={270}
          cy={y}
          r={7}
          fill={i === 0 ? 'rgba(45,212,191,0.2)' : 'rgba(79,141,255,0.08)'}
          stroke={i === 0 ? 'rgba(45,212,191,0.6)' : 'rgba(79,141,255,0.3)'}
          strokeWidth="1.5"
        />
      ))}

      {/* Connections L0→L1 (dim) */}
      {[95, 135, 175].map((y1, i) =>
        [82, 110, 138, 166, 194].map((y2, j) => (
          <line
            key={`c01-${i}-${j}`}
            x1={67}
            y1={y1}
            x2={123}
            y2={y2}
            stroke="rgba(79,141,255,0.06)"
            strokeWidth="0.7"
          />
        )),
      )}
      {/* Connections L1→L2 (dim) */}
      {[82, 110, 138, 166, 194].map((y1, i) =>
        [90, 122, 154, 186].map((y2, j) => (
          <line
            key={`c12-${i}-${j}`}
            x1={137}
            y1={y1}
            x2={193}
            y2={y2}
            stroke="rgba(79,141,255,0.06)"
            strokeWidth="0.7"
          />
        )),
      )}
      {/* Connections L2→L3 (dim) */}
      {[90, 122, 154, 186].map((y1, i) =>
        [120, 160].map((y2, j) => (
          <line
            key={`c23-${i}-${j}`}
            x1={207}
            y1={y1}
            x2={263}
            y2={y2}
            stroke="rgba(79,141,255,0.06)"
            strokeWidth="0.7"
          />
        )),
      )}

      {/* Highlighted path through network */}
      <line x1={67} y1={95} x2={123} y2={110} stroke="rgba(45,212,191,0.45)" strokeWidth="1.5" />
      <line x1={137} y1={110} x2={193} y2={122} stroke="rgba(45,212,191,0.45)" strokeWidth="1.5" />
      <line x1={207} y1={122} x2={263} y2={120} stroke="rgba(45,212,191,0.45)" strokeWidth="1.5" />
      {/* Highlighted nodes */}
      <circle
        cx={60}
        cy={95}
        r={7}
        fill="rgba(45,212,191,0.2)"
        stroke="rgba(45,212,191,0.6)"
        strokeWidth="1.5"
      />
      <circle
        cx={130}
        cy={110}
        r={7}
        fill="rgba(45,212,191,0.2)"
        stroke="rgba(45,212,191,0.6)"
        strokeWidth="1.5"
      />
      <circle
        cx={200}
        cy={122}
        r={7}
        fill="rgba(45,212,191,0.2)"
        stroke="rgba(45,212,191,0.6)"
        strokeWidth="1.5"
      />

      {/* Layer labels */}
      <text
        x={60}
        y={218}
        textAnchor="middle"
        fill="rgba(192,212,238,0.3)"
        fontSize="8"
        fontFamily="monospace"
      >
        input
      </text>
      <text
        x={165}
        y={218}
        textAnchor="middle"
        fill="rgba(192,212,238,0.3)"
        fontSize="8"
        fontFamily="monospace"
      >
        hidden
      </text>
      <text
        x={270}
        y={218}
        textAnchor="middle"
        fill="rgba(192,212,238,0.3)"
        fontSize="8"
        fontFamily="monospace"
      >
        output
      </text>

      {/* Output classification box */}
      <rect
        x={20}
        y={230}
        width={280}
        height={22}
        rx={4}
        fill="rgba(45,212,191,0.08)"
        stroke="rgba(45,212,191,0.25)"
        strokeWidth="1"
      />
      <text x={34} y={245} fill="rgba(45,212,191,0.7)" fontSize="10" fontFamily="monospace">
        class: safe_action
      </text>
      <text x={248} y={245} fill="rgba(45,212,191,0.5)" fontSize="10" fontFamily="monospace">
        0.94
      </text>
    </svg>
  );
}

/** Judge/jury verification: a minority reject escalates to a human. */
export function VerificationGraphic() {
  return (
    <svg
      viewBox="0 0 320 260"
      className="h-full w-full"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Title bar */}
      <rect
        x={20}
        y={10}
        width={280}
        height={28}
        rx={6}
        fill="rgba(25,45,85,0.6)"
        stroke="rgba(79,141,255,0.2)"
        strokeWidth="1"
      />
      <text x={34} y={28} fill="rgba(192,212,238,0.5)" fontSize="10" fontFamily="monospace">
        verification_pipeline
      </text>
      <circle cx={272} cy={24} r={4} fill="rgba(45,212,191,0.4)" />
      <circle cx={286} cy={24} r={4} fill="rgba(79,141,255,0.25)" />

      {/* Three judge cards */}
      {[
        { y: 56, label: 'Judge A', vote: 'APPROVE', color: 'teal' },
        { y: 118, label: 'Judge B', vote: 'APPROVE', color: 'teal' },
        { y: 180, label: 'Judge C', vote: 'REJECT', color: 'red' },
      ].map((judge, i) => (
        <g key={i}>
          <rect
            x={20}
            y={judge.y}
            width={120}
            height={48}
            rx={8}
            fill="rgba(25,45,85,0.5)"
            stroke="rgba(79,141,255,0.18)"
            strokeWidth="1"
          />
          {/* Avatar circle */}
          <circle
            cx={42}
            cy={judge.y + 24}
            r={10}
            fill="rgba(79,141,255,0.12)"
            stroke="rgba(79,141,255,0.3)"
            strokeWidth="1"
          />
          <text
            x={42}
            y={judge.y + 28}
            textAnchor="middle"
            fill="rgba(192,212,238,0.5)"
            fontSize="10"
          >
            {String.fromCharCode(65 + i)}
          </text>
          {/* Label */}
          <text x={60} y={judge.y + 20} fill="rgba(192,212,238,0.6)" fontSize="10">
            {judge.label}
          </text>
          {/* Vote badge */}
          <rect
            x={60}
            y={judge.y + 28}
            width={judge.vote === 'APPROVE' ? 56 : 48}
            height={16}
            rx={3}
            fill={judge.color === 'teal' ? 'rgba(45,212,191,0.12)' : 'rgba(239,68,68,0.12)'}
            stroke={judge.color === 'teal' ? 'rgba(45,212,191,0.35)' : 'rgba(239,68,68,0.3)'}
            strokeWidth="1"
          />
          <text
            x={judge.color === 'teal' ? 88 : 84}
            y={judge.y + 40}
            textAnchor="middle"
            fill={judge.color === 'teal' ? 'rgba(45,212,191,0.8)' : 'rgba(239,68,68,0.7)'}
            fontSize="8"
            fontFamily="monospace"
          >
            {judge.vote}
          </text>
          {/* Connection line to consensus */}
          <line
            x1={140}
            y1={judge.y + 24}
            x2={195}
            y2={148}
            stroke={judge.color === 'teal' ? 'rgba(45,212,191,0.25)' : 'rgba(239,68,68,0.15)'}
            strokeWidth="1"
            strokeDasharray={judge.color === 'red' ? '3 3' : 'none'}
          />
        </g>
      ))}

      {/* Consensus node — minority reject triggers escalation */}
      <circle
        cx={210}
        cy={148}
        r={18}
        fill="rgba(25,45,85,0.7)"
        stroke="rgba(239,68,68,0.35)"
        strokeWidth="1.5"
      />
      <text
        x={210}
        y={144}
        textAnchor="middle"
        fill="rgba(192,212,238,0.5)"
        fontSize="8"
        fontFamily="monospace"
      >
        1/3
      </text>
      <text
        x={210}
        y={156}
        textAnchor="middle"
        fill="rgba(239,68,68,0.7)"
        fontSize="8"
        fontFamily="monospace"
      >
        reject
      </text>

      {/* Reject path → escalate (primary, solid) */}
      <line x1={228} y1={156} x2={258} y2={196} stroke="rgba(239,68,68,0.35)" strokeWidth="1.5" />
      {/* Approve path → blocked (secondary, dashed) */}
      <line
        x1={228}
        y1={140}
        x2={258}
        y2={100}
        stroke="rgba(45,212,191,0.15)"
        strokeWidth="1"
        strokeDasharray="4 3"
      />

      {/* Proceed box — dimmed */}
      <rect
        x={258}
        y={80}
        width={52}
        height={40}
        rx={8}
        fill="rgba(45,212,191,0.05)"
        stroke="rgba(45,212,191,0.15)"
        strokeWidth="1"
      />
      <text x={284} y={98} textAnchor="middle" fill="rgba(45,212,191,0.4)" fontSize="16">
        {'\u2713'}
      </text>
      <text
        x={284}
        y={114}
        textAnchor="middle"
        fill="rgba(45,212,191,0.3)"
        fontSize="7"
        fontFamily="monospace"
      >
        blocked
      </text>

      {/* Escalate box — highlighted */}
      <rect
        x={258}
        y={180}
        width={52}
        height={40}
        rx={8}
        fill="rgba(239,68,68,0.08)"
        stroke="rgba(239,68,68,0.3)"
        strokeWidth="1"
      />
      <text x={284} y={198} textAnchor="middle" fill="rgba(239,68,68,0.7)" fontSize="8">
        {'\u270B'}
      </text>
      <text
        x={284}
        y={212}
        textAnchor="middle"
        fill="rgba(239,68,68,0.6)"
        fontSize="7"
        fontFamily="monospace"
      >
        escalate
      </text>

      {/* Bottom status bar */}
      <rect x={20} y={240} width={280} height={16} rx={3} fill="rgba(25,45,85,0.4)" />
      <text x={30} y={251} fill="rgba(239,68,68,0.5)" fontSize="8" fontFamily="monospace">
        minority_escalation: 1/3 reject → HUMAN REVIEW
      </text>
    </svg>
  );
}

/** Least-privilege shield feeding a running audit trail. */
export function AuditGraphic() {
  return (
    <svg
      viewBox="0 0 320 260"
      className="h-full w-full"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Shield — larger, left side */}
      <path
        d="M40 30 L40 120 Q40 165 80 185 Q120 165 120 120 L120 30 Z"
        fill="rgba(45,212,191,0.04)"
        stroke="rgba(45,212,191,0.3)"
        strokeWidth="1.5"
      />
      {/* Shield inner content — permission bars */}
      <rect x={52} y={52} width={56} height={6} rx={3} fill="rgba(45,212,191,0.35)" />
      <rect x={52} y={66} width={44} height={6} rx={3} fill="rgba(45,212,191,0.25)" />
      <rect x={52} y={80} width={32} height={6} rx={3} fill="rgba(45,212,191,0.15)" />
      <rect x={52} y={94} width={20} height={6} rx={3} fill="rgba(45,212,191,0.1)" />
      {/* Shield label */}
      <text
        x={80}
        y={120}
        textAnchor="middle"
        fill="rgba(45,212,191,0.5)"
        fontSize="9"
        fontFamily="monospace"
      >
        least
      </text>
      <text
        x={80}
        y={132}
        textAnchor="middle"
        fill="rgba(45,212,191,0.5)"
        fontSize="9"
        fontFamily="monospace"
      >
        privilege
      </text>
      {/* Lock icon */}
      <rect
        x={70}
        y={148}
        width={20}
        height={16}
        rx={3}
        fill="rgba(45,212,191,0.2)"
        stroke="rgba(45,212,191,0.4)"
        strokeWidth="1"
      />
      <path
        d="M75 148 L75 142 Q75 136 80 136 Q85 136 85 142 L85 148"
        stroke="rgba(45,212,191,0.5)"
        strokeWidth="1.5"
        fill="none"
      />

      {/* Arrow from shield to log panel */}
      <line x1={125} y1={108} x2={148} y2={108} stroke="rgba(192,212,238,0.2)" strokeWidth="1.5" />
      <polygon points="146,104 152,108 146,112" fill="rgba(192,212,238,0.2)" />

      {/* Audit log panel — right side */}
      <rect
        x={155}
        y={20}
        width={150}
        height={232}
        rx={10}
        fill="rgba(25,45,85,0.5)"
        stroke="rgba(79,141,255,0.18)"
        strokeWidth="1"
      />
      {/* Panel header */}
      <rect x={155} y={20} width={150} height={28} rx={10} fill="rgba(25,45,85,0.7)" />
      <rect x={155} y={38} width={150} height={2} fill="rgba(79,141,255,0.1)" />
      <text x={170} y={39} fill="rgba(192,212,238,0.5)" fontSize="9" fontFamily="monospace">
        audit_trail.log
      </text>
      <circle cx={288} cy={34} r={3} fill="rgba(45,212,191,0.4)" />

      {/* Log entries */}
      {[
        { y: 56, time: '14:32:01', text: 'action: classify', color: 'rgba(45,212,191,0.5)' },
        { y: 72, time: '14:32:01', text: 'confidence: 0.94', color: 'rgba(45,212,191,0.4)' },
        { y: 88, time: '14:32:02', text: 'judge_a: APPROVE', color: 'rgba(45,212,191,0.5)' },
        { y: 104, time: '14:32:02', text: 'judge_b: APPROVE', color: 'rgba(45,212,191,0.5)' },
        { y: 120, time: '14:32:03', text: 'judge_c: REJECT', color: 'rgba(239,68,68,0.45)' },
        { y: 136, time: '14:32:03', text: 'minority: escalate', color: 'rgba(239,68,68,0.45)' },
        { y: 152, time: '14:32:04', text: 'policy: checked', color: 'rgba(45,212,191,0.4)' },
        { y: 168, time: '14:32:04', text: 'scope: read_only', color: 'rgba(192,212,238,0.35)' },
        { y: 184, time: '14:32:05', text: 'action: executed', color: 'rgba(45,212,191,0.5)' },
        { y: 200, time: '14:32:05', text: 'rollback: ready', color: 'rgba(192,212,238,0.35)' },
        { y: 216, time: '14:32:06', text: 'art13: compliant', color: 'rgba(45,212,191,0.4)' },
        { y: 232, time: '14:32:06', text: 'trace_id: 7f3a..', color: 'rgba(192,212,238,0.3)' },
      ].map((entry, i) => (
        <g key={i} opacity={i > 8 ? 0.5 : 1}>
          <text
            x={165}
            y={entry.y + 10}
            fill="rgba(192,212,238,0.25)"
            fontSize="7.5"
            fontFamily="monospace"
          >
            {entry.time}
          </text>
          <text x={210} y={entry.y + 10} fill={entry.color} fontSize="8" fontFamily="monospace">
            {entry.text}
          </text>
        </g>
      ))}
    </svg>
  );
}
